import React, { useState, useRef, useEffect } from "react";
import {
  Upload,
  X,
  AlertCircle,
  RotateCcw,
  Maximize2,
  Trash2,
  Loader2,
  Info,
  Play,
  Film,
  Music,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { uploadFile } from "@/utils/oss";
import { toast } from "sonner";

interface OSSUploadProps {
  value?: string[];
  onChange?: (urls: string[]) => void;
  maxCount?: number;
  accept?: string;
  maxSize?: number; // MB为单位
  className?: string;
  folder?: string;
  disabled?: boolean;
  aspectRatio?: "square" | "video" | "free"; // 预览图比例
  placeholder?: string;
  fileNamePrefix?: string;
  customFileName?: string;
}

interface UploadingFile {
  id: string;
  file: File;
  status: "uploading" | "error" | "success";
  progress: number;
  preview?: string;
  error?: string;
  type: "image" | "video" | "other";
}

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
}

// 确认对话框组件
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 flex items-start gap-3">
          <div className="p-2 bg-red-100 rounded-full text-red-600">
            <Info className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <p className="mt-2 text-sm text-gray-500">{message}</p>
          </div>
        </div>
        <div className="bg-gray-50 px-5 py-3 flex justify-end gap-2">
          <button
            type="button"
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            onClick={onClose}
          >
            取消
          </button>
          <button
            type="button"
            className="px-3 py-1.5 bg-red-600 rounded-md text-sm font-medium text-white hover:bg-red-700 transition-colors"
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            确认删除
          </button>
        </div>
      </div>
    </div>
  );
};

// 视频预览对话框
interface VideoPreviewProps {
  url: string | null;
  onClose: () => void;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({ url, onClose }) => {
  if (!url) return null;

  const fullUrl = `${import.meta.env.VITE_OSS_HOST}${url}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="relative max-w-5xl w-[90%] h-[90%] flex items-center justify-center">
        <video
          src={fullUrl}
          className="max-w-full max-h-full"
          controls
          autoPlay
          onClick={(e) => e.stopPropagation()}
        >
          您的浏览器不支持视频播放。
        </video>
        <button
          className="absolute top-4 right-4 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
          onClick={onClose}
        >
          <X className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};

const AudioPreview: React.FC<VideoPreviewProps> = ({ url, onClose }) => {
  if (!url) return null;

  const fullUrl = `${import.meta.env.VITE_OSS_HOST}${url}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="relative max-w-5xl w-[90%] h-[90%] flex items-center justify-center">
        <audio
          src={fullUrl}
          className="max-w-full max-h-full"
          controls
          autoPlay
          onClick={(e) => e.stopPropagation()}
        >
          您的浏览器不支持音频播放。
        </audio>
        <button
          className="absolute top-4 right-4 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
          onClick={onClose}
        >
          <X className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};

export const OSSUpload: React.FC<OSSUploadProps> = ({
  value = [],
  onChange,
  maxCount = 1,
  accept = "image/*",
  maxSize = 5,
  className,
  folder = "default",
  disabled = false,
  aspectRatio = "square",
  placeholder = "上传图片",
  fileNamePrefix = "",
  customFileName,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);
  const [previewAudio, setPreviewAudio] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    url?: string;
    isAllFiles: boolean;
  }>({ isOpen: false, isAllFiles: false });
  const inputRef = useRef<HTMLInputElement>(null);

  // 判断文件类型
  const getFileType = (file: File): "image" | "video" | "other" => {
    if (file.type.startsWith("image/")) {
      return "image";
    } else if (file.type.startsWith("video/")) {
      return "video";
    }
    return "other";
  };

  // 清理预览URL
  useEffect(() => {
    return () => {
      uploadingFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [uploadingFiles]);

  // 创建预览URL
  const createPreview = (file: File): string => {
    return URL.createObjectURL(file);
  };

  // 文件验证
  const validateFile = (file: File) => {
    // 检查文件大小
    if (file.size > maxSize * 1024 * 1024) {
      return {
        valid: false,
        error: `文件大小超过 ${maxSize}MB 限制`,
      };
    }

    // 检查文件类型
    if (accept !== "*") {
      const acceptTypes = accept.split(",").map((type) => type.trim());
      const fileType = file.type;

      // 处理特殊的 accept 格式，如 image/*
      const isValid = acceptTypes.some((type) => {
        if (type.endsWith("/*")) {
          const mainType = type.split("/")[0];
          return fileType.startsWith(`${mainType}/`);
        }
        return type === fileType;
      });

      if (!isValid) {
        let typesText = acceptTypes.join(", ");
        // 使文件类型更友好显示
        typesText = typesText.replace("image/*", "图片");
        typesText = typesText.replace("video/*", "视频");
        typesText = typesText.replace("audio/*", "音频");

        return {
          valid: false,
          error: `只支持 ${typesText} 类型文件`,
        };
      }
    }

    return { valid: true };
  };

  // 创建自定义路径
  const getFileExtension = (filename: string) => {
    const lastDotIndex = filename.lastIndexOf(".");
    return lastDotIndex === -1 ? "" : filename.slice(lastDotIndex); // 包含 '.'
  };

  // 判断URL是否为视频
  const isVideoUrl = (url: string): boolean => {
    const videoExtensions = [".mp4", ".webm", ".mov", ".avi", ".mkv"];
    return videoExtensions.some((ext) => url.toLowerCase().endsWith(ext));
  };
  const isAudioUrl = (url: string): boolean => {
    const videoExtensions = [
      ".mp3",
      ".wma",
      ".wav",
      ".m4a",
      ".aif",
      ".aiff",
      "au",
      ".ra",
      ".rm",
      ".ram",
    ];
    return videoExtensions.some((ext) => url.toLowerCase().endsWith(ext));
  };

  // 处理文件上传
  const handleUpload = async (file: File) => {
    // 验证文件
    const validation = validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return { success: false, error: validation.error };
    }

    const preview = createPreview(file);
    const fileId = `upload-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const fileType = getFileType(file);

    // 添加上传中文件
    const newFile: UploadingFile = {
      id: fileId,
      file,
      status: "uploading",
      progress: 0,
      preview,
      type: fileType,
    };

    setUploadingFiles((prev) => [...prev, newFile]);
    setIsUploading(true);

    try {
      // 创建自定义路径
      const fileExtension = getFileExtension(file.name);

      // ✅ 修改文件名生成逻辑
      let fileName: string;

      if (customFileName) {
        // 使用完全自定义的文件名（不含扩展名）
        fileName = customFileName;
      } else {
        // 只使用 UUID（已足够唯一）
        const uuid = crypto.randomUUID();
        fileName = fileNamePrefix ? `${fileNamePrefix}_${uuid}` : uuid;
      }

      const customPath = `${folder}${fileName}${fileExtension}`;

      // 上传文件
      const url = await uploadFile(
        file,
        (progress) => {
          setUploadingFiles((prev) =>
            prev.map((item) =>
              item.id === fileId ? { ...item, progress } : item
            )
          );
        },
        customPath
      );

      // 更新状态
      setUploadingFiles((prev) =>
        prev.map((item) =>
          item.id === fileId ? { ...item, status: "success" } : item
        )
      );

      // 更新值
      const newUrls = [...value, url];
      onChange?.(newUrls);

      // 成功通知
      if (value.length === 0) {
        toast.success("文件上传成功");
      }

      return { success: true, url };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "上传失败";

      // 更新状态
      setUploadingFiles((prev) =>
        prev.map((item) =>
          item.id === fileId
            ? { ...item, status: "error", error: errorMessage }
            : item
        )
      );

      toast.error(`上传失败: ${errorMessage}`);
      return { success: false, error: errorMessage };
    } finally {
      setIsUploading(false);
    }
  };

  // 处理文件选择
  const handleFileSelect = async (selectedFiles: FileList | null) => {
    if (!selectedFiles || disabled) return;

    const remainingSlots = maxCount - value.length;
    if (remainingSlots <= 0) {
      toast.error(`最多只能上传 ${maxCount} 个文件`);
      return;
    }

    const filesToUpload = Array.from(selectedFiles).slice(0, remainingSlots);

    if (filesToUpload.length > 0) {
      for (const file of filesToUpload) {
        await handleUpload(file);
      }
    }
  };

  // 处理拖拽
  const handleDrag = (e: React.DragEvent) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // 处理拖拽放下
  const handleDrop = async (e: React.DragEvent) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const { files } = e.dataTransfer;
    await handleFileSelect(files);
  };

  // 重试上传
  const handleRetry = async (fileItem: UploadingFile) => {
    if (disabled) return;

    // 移除失败文件
    setUploadingFiles((prev) => prev.filter((f) => f.id !== fileItem.id));

    // 重新上传
    await handleUpload(fileItem.file);
  };

  // 打开删除确认对话框
  const openDeleteConfirm = (url: string) => {
    setConfirmDialog({
      isOpen: true,
      url,
      isAllFiles: false,
    });
  };

  // 打开清空所有确认对话框
  const openClearAllConfirm = () => {
    setConfirmDialog({
      isOpen: true,
      isAllFiles: true,
    });
  };

  // 删除文件
  const handleDelete = (url: string) => {
    console.log(url);
    if (disabled) return;

    // 从值中移除
    const newUrls = value.filter((item) => item !== url);
    onChange?.(newUrls);

    console.log("onChange 已调用");
  };

  // 删除正在上传的文件
  const handleRemoveUploading = (fileId: string) => {
    setUploadingFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  };

  // 预览图片或视频
  const handlePreview = (url: string) => {
    url = `${folder}${url}`;
    if (isVideoUrl(url)) {
      setPreviewVideo(url);
    } else if (isAudioUrl(url)) {
      setPreviewAudio(url);
    } else {
      setPreviewImage(url);
    }
  };

  // 清空所有
  const handleClearAll = () => {
    if (disabled || value.length === 0) return;
    onChange?.([]);
  };

  // 获取展示尺寸类名
  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case "square":
        return "aspect-square";
      case "video":
        return "aspect-video";
      default:
        return "";
    }
  };

  // 渲染上传区域
  const renderUploadArea = () => {
    if (value.length >= maxCount) return null;

    return (
      <div
        className={cn(
          "mt-4 relative border-2 border-dashed rounded-lg transition-all",
          getAspectRatioClass(),
          "flex flex-col items-center justify-center text-center p-6 group",
          dragActive
            ? "border-primary/70 bg-primary/5"
            : "border-gray-200 hover:border-primary/50 hover:bg-gray-50/50",
          disabled &&
            "opacity-60 cursor-not-allowed pointer-events-none bg-gray-50",
          className
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={maxCount > 1}
          accept={accept}
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files)}
          disabled={disabled || isUploading}
        />

        {isUploading ? (
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
            <p className="mt-2 text-sm text-gray-500">上传中...</p>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
              <Upload className="h-6 w-6 text-primary/70" />
            </div>
            <h3 className="text-base font-medium text-gray-700">
              {placeholder}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              拖放文件或 <span className="text-primary">浏览</span>
            </p>
            <p className="mt-2 text-xs text-gray-400">
              {accept.includes("image") && accept.includes("video")
                ? "支持的格式: JPG, PNG, GIF, MP4, WebM"
                : accept.includes("image")
                ? "支持的图片格式: JPG, PNG, GIF"
                : accept.includes("video")
                ? "支持的视频格式: MP4, WebM, MOV"
                : ""}
              {maxCount > 1 ? `, 最多 ${maxCount} 个文件` : ""}
            </p>
            <p className="text-xs text-gray-400">最大文件大小: {maxSize}MB</p>
          </>
        )}
      </div>
    );
  };

  // 渲染已上传文件列表
  const renderFileList = () => {
    if (value.length === 0 && uploadingFiles.length === 0) return null;

    return (
      <div className="mt-4 space-y-4 flex flex-col">
        {/* 标题和操作按钮 */}
        {value.length > 0 && (
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium text-gray-700">
              已上传文件 ({value.length}/{maxCount})
            </h4>
            {value.length > 1 && (
              <button
                type="button"
                onClick={openClearAllConfirm}
                disabled={disabled}
                className={cn(
                  "text-xs text-red-500 hover:text-red-700 transition-colors flex items-center gap-1",
                  disabled && "opacity-50 cursor-not-allowed"
                )}
              >
                <Trash2 className="h-3 w-3" />
                清空全部
              </button>
            )}
          </div>
        )}

        {/* 文件网格 */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {/* 已上传的文件 */}
          {value.map((url) => {
            const isVideo = isVideoUrl(url);
            const isAudio = isAudioUrl(url);
            return (
              <div
                key={url}
                className={cn(
                  "group relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50",
                  getAspectRatioClass(),
                  "transition-all hover:shadow-md",
                  disabled && "opacity-70 pointer-events-none"
                )}
              >
                {isVideo ? (
                  <div className="w-full h-full min-h-40 flex items-center justify-center relative bg-gray-100">
                    <Film className="h-8 w-8 text-gray-400 absolute" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
                        onClick={() => handlePreview(url)}
                      >
                        <Play className="h-6 w-6 text-white" />
                      </button>
                    </div>
                    <video
                      src={`${import.meta.env.VITE_OSS_HOST}${folder}${url}`}
                      className="w-full h-full object-cover opacity-80"
                      onError={(e) => {
                        // 视频加载失败，显示占位符
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                ) : isAudio ? (
                  <div className="w-full h-full min-h-40 flex items-center justify-center relative bg-gray-100">
                    <Music className="h-8 w-8 text-gray-400 absolute" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
                        onClick={() => handlePreview(url)}
                      >
                        <Play className="h-6 w-6 text-white" />
                      </button>
                    </div>
                    <audio
                      src={`${import.meta.env.VITE_OSS_HOST}${folder}${url}`}
                      className="w-full h-full object-cover opacity-80"
                      onError={(e) => {
                        // 音频加载失败，显示占位符
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                ) : (
                  <img
                    src={`${import.meta.env.VITE_OSS_HOST}${folder}${url}`}
                    alt="uploaded"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23f5f5f5"/%3E%3Cpath d="M35 40L65 60M65 40L35 60" stroke="%23ccc" stroke-width="2"/%3E%3Ctext x="50" y="80" font-family="Arial" font-size="8" text-anchor="middle" fill="%23999"%3E加载失败%3C/text%3E%3C/svg%3E';
                    }}
                  />
                )}

                {/* 文件类型标签 */}
                {isVideo && (
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                    视频
                  </div>
                )}

                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {/* 删除按钮 */}
                  <button
                    type="button"
                    className="p-1.5 rounded-full bg-white/90 hover:bg-white text-red-500 hover:text-red-600 transition-colors"
                    onClick={() => openDeleteConfirm(url)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>

                  {/* 预览按钮 */}
                  <button
                    type="button"
                    className="p-1.5 rounded-full bg-white/90 hover:bg-white text-blue-500 hover:text-blue-600 transition-colors"
                    onClick={() => handlePreview(url)}
                  >
                    {isVideo ? (
                      <Play className="h-4 w-4" />
                    ) : isAudio ? (
                      <Play className="h-4 w-4" />
                    ) : (
                      <Maximize2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}

          {/* 正在上传的文件 */}
          {uploadingFiles
            .filter((file) => file.status !== "success")
            .map((file) => (
              <div
                key={file.id}
                className={cn(
                  "relative border border-gray-200 rounded-lg overflow-hidden min-h-32",
                  getAspectRatioClass(),
                  file.status === "error"
                    ? "border-red-200 bg-red-50/50"
                    : "bg-gray-50"
                )}
              >
                {file.preview && file.type === "image" && (
                  <img
                    src={file.preview}
                    alt="preview"
                    className={cn(
                      "w-full h-full object-cover",
                      file.status === "error" && "opacity-50"
                    )}
                  />
                )}
                {file.preview && file.type === "video" && (
                  <div className="relative w-full h-full">
                    <video
                      src={file.preview}
                      className={cn(
                        "w-full h-full object-cover",
                        file.status === "error" && "opacity-50"
                      )}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Film className="h-10 w-10 text-white/80" />
                    </div>
                  </div>
                )}

                {/* 文件类型标签 */}
                {file.type === "video" && (
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded z-10">
                    视频
                  </div>
                )}

                <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                  {file.status === "uploading" ? (
                    <>
                      <div className="w-12 h-12 rounded-full bg-black/30 flex items-center justify-center mb-1">
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                      </div>
                      <div className="mt-2 text-sm font-medium text-gray-800 bg-white/80 px-2 py-0.5 rounded">
                        {file.progress}%
                      </div>
                      <div className="mt-1 w-3/4">
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-300 rounded-full"
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="absolute top-2 right-2 p-1 rounded-full bg-white/80 hover:bg-white text-gray-600"
                        onClick={() => handleRemoveUploading(file.id)}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : file.status === "error" ? (
                    <>
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-1">
                        <AlertCircle className="h-6 w-6 text-red-500" />
                      </div>
                      <p className="text-sm font-medium text-red-600 text-center mt-2">
                        上传失败
                      </p>
                      {file.error && (
                        <p className="text-xs text-red-500/80 text-center mt-1 max-w-full truncate">
                          {file.error}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          type="button"
                          className="px-2 py-1 bg-white border border-gray-200 rounded text-xs hover:bg-gray-50 transition-colors flex items-center gap-1"
                          onClick={() => handleRemoveUploading(file.id)}
                        >
                          <X className="h-3 w-3" />
                          移除
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1 bg-primary text-white rounded text-xs hover:bg-primary/90 transition-colors flex items-center gap-1"
                          onClick={() => handleRetry(file)}
                        >
                          <RotateCcw className="h-3 w-3" />
                          重试
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full">
      {/* 文件列表 */}
      {renderFileList()}

      {/* 上传区域 */}
      {renderUploadArea()}

      {/* 图片预览模态框 */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-5xl w-[90%] h-[90%] flex items-center justify-center">
            <img
              src={`${import.meta.env.VITE_OSS_HOST}/${previewImage}`}
              alt="preview"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className="absolute top-4 right-4 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
              onClick={() => setPreviewImage(null)}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}

      {/* 视频预览模态框 */}
      <VideoPreview url={previewVideo} onClose={() => setPreviewVideo(null)} />
      {/* 音频预览模态框 */}
      <AudioPreview url={previewAudio} onClose={() => setPreviewAudio(null)} />

      {/* 确认删除对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={() => {
          if (confirmDialog.isAllFiles) {
            handleClearAll();
          } else if (confirmDialog.url) {
            handleDelete(confirmDialog.url);
          }
        }}
        title={confirmDialog.isAllFiles ? "清空所有文件" : "删除文件"}
        message={
          confirmDialog.isAllFiles
            ? "确定要删除所有已上传的文件吗？此操作不可恢复。"
            : "确定要删除这个文件吗？此操作不可恢复。"
        }
      />
    </div>
  );
};
